import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Button, Card, HStack, SimpleGrid, Spacer, Text, VStack } from '@chakra-ui/react';
import { OpenSeatMatchCard } from '@src/components/sco/Advisors/cards/OpenSeatMatchCard';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { OpportunitiesAccessGate, OpportunitiesLayout } from '@src/components/sco/Advisors/layout/OpportunitiesLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { filterOpenSeatMatches } from '@src/components/sco/Advisors/utils/openSeatMatches';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_MATCHED_OPEN_SEATS } from '@src/gql/queries/AdvisorMatchedOpenSeats';
import { ScoUserMatchCollection } from '@src/graphql-types';
import { useMemo } from 'react';
const PAGE_SIZE = 48;
type MatchesData = {
  advisorMatchedOpenSeats?: ScoUserMatchCollection | null;
};
const MatchesBody = () => {
  const $ = _c(18);
  const paths = useAdvisorsPaths();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      variables: {
        pageSize: PAGE_SIZE,
        page: 1
      },
      fetchPolicy: "network-only"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const query = useQuery(ADVISOR_MATCHED_OPEN_SEATS, t0);
  query.data?.advisorMatchedOpenSeats?.items;
  const t1 = query.data?.advisorMatchedOpenSeats?.items;
  let t2;
  if ($[1] !== t1) {
    t2 = filterOpenSeatMatches(t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const firstPageMatches = t2;
  const t3 = query.data?.advisorMatchedOpenSeats?.pagination?.hasNextPage;
  let t4;
  if ($[3] !== firstPageMatches || $[4] !== query.fetchMore || $[5] !== t3) {
    t4 = {
      fetchMore: query.fetchMore,
      field: "advisorMatchedOpenSeats",
      firstPageItems: firstPageMatches,
      hasNextPage: t3
    };
    $[3] = firstPageMatches;
    $[4] = query.fetchMore;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    items: matchedSeats,
    loadMore,
    loadingMore,
    hasNextPage
  } = useAdvisorsCollectionLoadMore(t4);
  const itemCount = query.data?.advisorMatchedOpenSeats?.pagination?.itemCount ?? matchedSeats.length;
  const t5 = query.loading && !query.data;
  let t6;
  if ($[7] !== hasNextPage || $[8] !== itemCount || $[9] !== loadMore || $[10] !== loadingMore || $[11] !== matchedSeats || $[12] !== paths) {
    t6 = matchedSeats.length === 0 ? <Card.Root variant="subtle"><Card.Body gap={2}><Text color="colorPalette.fg" fontWeight="semibold">No matched seats yet.</Text><Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">When founders publish open seats that match your categories and skills, they show up here with founder context and a match score.</Text><Text color="colorPalette.fg/50" fontFamily="longform" fontSize="xs">Tip: keep categories and preferred compensation up to date on your advisor profile.</Text><Spacer /><Button asChild={true} alignSelf="flex-start" size="sm" variant="outline"><NextLink href={paths.ownProfile}>Update advisor profile</NextLink></Button></Card.Body></Card.Root> : <VStack align="stretch" gap={3}><HStack justify="space-between" wrap="wrap"><Text color="colorPalette.fg/60" fontSize="sm">Open a card to see the founder, company, and apply or message.</Text><Text color="colorPalette.fg/60" textStyle="labelXs" whiteSpace="nowrap">{itemCount} matched</Text></HStack><SimpleGrid columns={{
        base: 1,
        md: 2,
        xl: 3
      }} gap={4}>{matchedSeats.map(_temp)}</SimpleGrid><AdvisorsLoadMore hasNextPage={hasNextPage} loading={loadingMore} onLoadMore={loadMore} /></VStack>;
    $[7] = hasNextPage;
    $[8] = itemCount;
    $[9] = loadMore;
    $[10] = loadingMore;
    $[11] = matchedSeats;
    $[12] = paths;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== query.error || $[15] !== t5 || $[16] !== t6) {
    t7 = <AdvisorsQueryState error={query.error} loading={t5}>{t6}</AdvisorsQueryState>;
    $[14] = query.error;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};

/** `/advisors/opportunities/matches` — reverse matches: open seats that ranked you. */
export const OpportunitiesMatchesSection = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <OpportunitiesAccessGate><OpportunitiesLayout activeKey="matches" description="Founders whose open board seats ranked you as a fit (the reverse of their seat search)." title="Matched seats"><MatchesBody /></OpportunitiesLayout></OpportunitiesAccessGate>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(match) {
  return <OpenSeatMatchCard key={match.userMatchId} match={match} />;
}